import { CreateLogger } from '@core/shared';
import { useSiteSettingsContext } from '@core/shared/src/components/SiteSettingsProvider';
import { I18nProvider, LoaderPage } from '@core/ui';
import { PageNotFound } from '@online-webinar/account';
import { ErrorComponent, getSiteSettings, getSiteSettingsScript, HeadContent, RouterType, Scripts } from '@online-webinar/router';
import { store } from '@online-webinar/store';
import { type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { DocumentTitle } from '../components/DocumentTitle';
import { Layout } from '../components/Layout';
import { LayoutWhitelabel } from '../components/LayoutWhitelabel';
import { RootMaintenanceNotice } from '../components/RootMaintenanceNotice';
import { AuthProvider } from '../integrations/auth-provider';
import { SiteSettingsProvider } from '../integrations/site-settings-provider';

export const updateRoute = (router: RouterType) => {
    router.routesById['__root__'].update({
        head: () => {
            return {
                meta: [
                    {
                        charSet: 'utf-8'
                    },
                    {
                        name: 'viewport',
                        content: 'width=device-width, initial-scale=1'
                    },
                    {
                        name: 'theme-color',
                        content: '#000000'
                    }
                    // No title here: DocumentTitle renders it, and a second <title> would win (the browser takes
                    // the first).
                ],
                links: [
                    { rel: 'stylesheet', href: '/theme.css' },
                    { rel: 'stylesheet', href: '/site.css' },
                    { rel: 'icon', href: '/favicon.ico' },
                    { rel: 'icon', href: '/icon.svg' },
                    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
                    { rel: 'manifest', href: '/manifest.webmanifest' }
                ]
            };
        },
        headers: () => {
            let headers = {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Access-Control-Allow-Headers': '*',
                'Access-Control-Allow-Credentials': 'true',

                'X-Frame-Options': 'SAMEORIGIN',
                'X-Content-Type-Options': 'nosniff',
                'X-XSS-Protection': '1; mode=block',
                'Referrer-Policy': 'no-referrer',

                'Cache-Control': 'public, must-revalidate, max-age=0'
            };
            const siteSettings = getSiteSettings();
            if (!siteSettings.isProduction) {
                headers = { ...headers, ...{ 'X-Robots-Tag': 'none' } };
            }
            return headers;
        },
        component: RootComponent,
        notFoundComponent: PageNotFound,
        errorComponent: ({ error, reset }) => <ErrorComponent error={error} />,
        pendingComponent: LoaderPage,
        scripts: getSiteSettingsScript
    });
};

function RootComponent() {
    return (
        <RootDocument>
            <SiteSettingsProvider>
                <AuthProvider>
                    <I18nProvider>
                        <DocumentTitle />
                        <Provider store={store}>
                            <ToastContainer position="bottom-center" autoClose={false} hideProgressBar={true} draggable closeOnClick={false} />
                            <CreateLogger />
                            <RootMaintenanceNotice />
                            <ChooseLayout />
                        </Provider>
                    </I18nProvider>
                </AuthProvider>
            </SiteSettingsProvider>
        </RootDocument>
    );
}

function ChooseLayout() {
    const siteSettings = useSiteSettingsContext();
    if (siteSettings.isWhitelabel) {
        return <LayoutWhitelabel logoUrl={siteSettings.logoUrl!} logoText={siteSettings.logoText} footerLinks={siteSettings.footerLinks!} />;
    } else {
        return <Layout />;
    }
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
    const siteSettings = getSiteSettings();
    return (
        <html lang={siteSettings.defaultLanguage}>
            <head>
                <HeadContent />
            </head>
            <body>
                {children}
                <Scripts />
            </body>
        </html>
    );
}
